import React, { useEffect, useState } from 'react';
import { getCerts, updateCert, deleteCertById } from '../../features/certs/certSlice';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Form, 
  Input, 
  Modal, 
  Button, 
  Card, 
  Typography, 
  
  message, 
  Alert,
  Divider,
  Tag,
  Tooltip
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  BankOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const RenderCert = () => {
  const { certs } = useSelector((state) => state.certs);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editCert, setEditCert] = useState(null);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    dispatch(getCerts());
  }, [dispatch]);

  const handleEditClick = (id) => {
    setIsEditModalVisible(true);
    const certToEdit = certs.find((cert) => cert._id === id);
    setEditCert(certToEdit);
    form.setFieldsValue({
      title: certToEdit.title,
      institute: certToEdit.institute,
      link: certToEdit.link,
    });
  };

  const handleUpdateCert = async (values) => {
    try {
      await dispatch(updateCert({ id: editCert._id, formParams: values }));
      dispatch(getCerts());
      setIsEditModalVisible(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      form.resetFields();
    } catch (err) {
      console.log('Error encountered trying to update', err);
      setFailed(true);
      setTimeout(() => {
        setFailed(false);
      }, 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleDeleteCert = (id) => {
    dispatch(deleteCertById(id))
      .then((result) => {
        console.log(result);
        dispatch(getCerts());
        message.success('Certificate deleted successfully');
      })
      .catch((error) => {
        console.log('Problem deleting');
        message.error('Failed to delete certificate');
      });
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditModalVisible(false);
    setEditCert(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <SafetyCertificateOutlined className="text-2xl text-accent-blue" />
          <Title level={2} className="m-0 text-text-primary font-semibold">
            Certificates & Credentials
          </Title>
        </div>
        <Text className="text-text-secondary text-base">
          Manage and showcase your professional certifications and achievements
        </Text>
      </div>

      {/* Alert Messages */}
      {success && (
        <Alert
          className="mb-6 border border-accent-green bg-green-50 rounded-lg"
          message="Certificate updated successfully!"
          description="Your changes have been saved to your portfolio."
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}
      
      {failed && (
        <Alert
          className="mb-6 border border-accent-red bg-red-50 rounded-lg"
          message="Update Failed"
          description="There was an error updating your certificate. Please try again."
          type="error"
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Certificates Grid */}
      {certs && certs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certs.map((cert) => (
            <Card
              key={cert._id}
              className="shadow-medium border border-border-light rounded-xl hover:shadow-hard transition-all duration-300 bg-white"
              bodyStyle={{ padding: '24px' }}
            >
              <div className="flex flex-col h-full">
                {/* Certificate Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafetyCertificateOutlined className="text-accent-blue text-lg" />
                      <Tag color="blue" className="text-xs">
                        CERTIFIED
                      </Tag>
                    </div>
                    <Title level={4} className="text-text-primary mb-1 line-clamp-2">
                      {cert.title}
                    </Title>
                  </div>
                </div>

                {/* Institute Info */}
                <div className="flex items-center space-x-2 mb-4">
                  <BankOutlined className="text-neutral-400" />
                  <Text className="text-text-secondary text-sm font-medium">
                    {cert.institute}
                  </Text>
                </div>

                {/* Certificate Link */}
                {cert.link && (
                  <div className="mb-4">
                    <a
                      href={cert.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-accent-blue hover:text-accent-blue/80 text-sm font-medium transition-colors duration-200"
                    >
                      <LinkOutlined />
                      <span>View Certificate</span>
                    </a>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-2 mt-auto pt-4 border-t border-border-light">
                  <Tooltip title="Edit Certificate">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditClick(cert._id)}
                      className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10"
                    >
                      Edit
                    </Button>
                  </Tooltip>
                  <Tooltip title="Delete Certificate">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteCert(cert._id)}
                      className="text-accent-red hover:text-accent-red/80 hover:bg-accent-red/10"
                      danger
                    >
                      Delete
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <SafetyCertificateOutlined className="text-6xl text-neutral-400 mb-4" />
          <Title level={3} className="text-neutral-500 mb-2">
            No Certificates Yet
          </Title>
          <Text className="text-neutral-400">
            Start adding your professional certifications to showcase your expertise.
          </Text>
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2 pb-4 border-b border-border-light">
            <EditOutlined className="text-accent-blue" />
            <span className="text-text-primary font-semibold">Edit Certificate</span>
          </div>
        }
        open={isEditModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="mr-2">
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleOk}
            className="bg-accent-blue hover:bg-accent-blue/90 border-accent-blue"
          >
            Update Certificate
          </Button>
        ]}
        className="edit-cert-modal-right"
        destroyOnClose
        maskClosable={false}
      >
        <div className="pt-4">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdateCert}
            onFinishFailed={onFinishFailed}
            size="large"
            className="space-y-4"
          >
            {/* Basic Information */}
            <div className="mb-6">
              <Title level={5} className="text-text-primary mb-4 flex items-center">
                <CheckCircleOutlined className="mr-2 text-accent-blue" />
                Certificate Information
              </Title>
              
              <Form.Item 
                label={<span className="text-text-primary font-medium">Certificate Title</span>}
                name="title"
                rules={[{ required: true, message: 'Please enter the certificate title' }]}
              >
                <Input 
                  placeholder="e.g., AWS Certified Solutions Architect"
                  className="h-10 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
                />
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Issuing Organization</span>}
                name="institute"
                rules={[{ required: true, message: 'Please enter the issuing organization' }]}
              >
                <Input 
                  placeholder="e.g., Amazon Web Services"
                  prefix={<BankOutlined className="text-neutral-400" />}
                  className="h-10 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
                />
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Certificate Link</span>}
                name="link"
                rules={[
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input 
                  placeholder="https://credentials.example.com/certificate-id"
                  prefix={<LinkOutlined className="text-neutral-400" />}
                  className="h-10 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
                />
              </Form.Item>
            </div>

            <Divider className="border-border-light my-4" />

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Text className="text-text-secondary text-sm">
                <strong>💡 Pro Tip:</strong> Include the direct link to your certificate for easy verification by potential employers or clients.
              </Text>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default RenderCert;